/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { DOMElementsStringifyReplacer } from './util.js';



export function SimpleFunctionCallScheduler( runTaskCallback, enableDebugInfo ) {
	this._runTaskCallback = runTaskCallback;
	this._timeout_id = null;
	this._scheduled_args = null;		// available as long as _timeout_id!==null
	// this._scheduled_execDate = null;	// available as long as _timeout_id!==null
	this._defered = null;
	this._running = false;
	this._enableDebugInfo = enableDebugInfo;

	let that = this;
	// this._on_call_done_expected = false;

	this._on_call_done = function _on_call_done() {
		that._running = false;
		if( that._defered ) {
			let defered_timeout_ms = Math.max(4,that._defered.execDate-Date.now());
			let defered_args = that._defered.args;
			that._defered = null;
			that._schedule1(defered_timeout_ms,defered_args);
		}
	};
	this._on_scheduled = function _on_scheduled() {
		let args = that._scheduled_args;
		that._timeout_id = null;
		that._scheduled_args = null;
		// that._scheduled_execDate = null;
		that._running = true;
		try {
			that._runTaskCallback.call(that,that._on_call_done,args);
		} catch( e ) {
			if( that._running ) {
				// exception occured before _on_call_done() ..
				// -> auto-call _on_call_done() here now, and thus
				//    allow scheduler to continue !
				that._on_call_done();
			}
			// but rethrow the exception anyway
			throw e;
		}
	};
	this._schedule1 = function _schedule1( timeout_ms, args ) {
		timeout_ms = Math.max(4,timeout_ms);
		that._scheduled_args = args;
		// that._scheduled_execDate = Date.now() + timeout_ms;
		that._timeout_id = window.setTimeout(that._on_scheduled,timeout_ms);
	};
	this._cancel1 = function _cancel1() {
		window.clearTimeout(that._timeout_id);
		that._timeout_id = null;
		that._scheduled_args = null;
		// that._scheduled_execDate = null;
	};
}

SimpleFunctionCallScheduler.prototype.schedule = function schedule( timeout_ms, args ) {
	//console.log(['SimpleFunctionCallScheduler.schedule', [this._running,this._defered,this._timeout_id], timeout_ms ]);
	if( this._running ) {
		// a call is already running -> set _defered
		let newDefered = { execDate: Date.now()+timeout_ms, args };

		if( this._defered && this._enableDebugInfo ) {
			// warn about DROPPING defered tasks
			console.warn(
				'SimpleFunctionCallScheduler.schedule(): another task is running, so will "defer" this task now - which means, will run it\n'
				+  ' immediately after the active task is finished.\n'
				+  '\n'
				+  'But NOTICE:\n'
				+  ' Current implementation supports a backlog of exactly ONE defered task only - there is no list or queue of defered tasks.\n'
				+  ' This is an intentional design choice - SimpleFunctionCallScheduler works best for scheduling "disposable" UI tasks.\n'
				+  ' It is not designed as a task queue!\n'
				+  '\n'
				+  'FYI:\n'
				+  '  - dropping previously defered task:  %O\n'
				+  '  - replacing with new defered task:   %O\n'
				+  '\n\n',
				this._defered,
				newDefered
			);
		}

		this._defered = newDefered;
		return;
	} else if( this._timeout_id!==null ) {

		if( this._enableDebugInfo ) {
			// warn about DROPPING former scheduled tasks
			console.warn(
				'SimpleFunctionCallScheduler.schedule(): another task is scheduled already. Will drop that task now and replace it with the new task.\n'
				+  '\n'
				+  'But NOTICE:\n'
				+  ' Current implementation supports no backlog (except one slot for a defered task) - there is no list or queue of scheduled tasks.\n'
				+  ' This is an intentional design choice - SimpleFunctionCallScheduler works best for scheduling "disposable" UI tasks.\n'
				+  ' It is not designed as a task queue!\n'
				+  '\n'
				+  'FYI:\n'
				+  '  - dropping previously scheduled task:  [task data n/a]\n'
				+  '  - scheduling new task:                 %O\n'
				+  '\n\n',
				{timeout_ms,args}
			);
		}

		// cancel current timeout and ..
		this._cancel1();
	}
	// schedule the call
	this._schedule1(timeout_ms,args);
};

SimpleFunctionCallScheduler.prototype.cancel = function cancel() {
	//console.log(['SimpleFunctionCallScheduler.cancel', [this._running,this._defered,this._timeout_id] ]);
	if( this._defered ) this._defered = null; // cancel defered call
	if( this._timeout_id!==null ) this._cancel1(); // cancel upcoming call
};

SimpleFunctionCallScheduler.prototype.getState = function getState() {
	if( this._defered ) return 'defered';				// a task is running and another task was already scheduled as "defered"
	if( this._running ) return 'running';				// a task is running
	if( this._timeout_id!==null ) return 'scheduled';	// a task is scheduled (will execute after individual timeout)
	return 'idle';										// no task scheduled nor running
};

SimpleFunctionCallScheduler.prototype.getScheduledTask = function getScheduledTask() {
	return {
		// execDate: this._scheduled_execDate,
		args: this._scheduled_args
	};
};










/**
 * ChainedTasksScheduler constructor.
 *
 * Based on SimpleFunctionCallScheduler.
 *
 * Schedules "typed" tasks, and chains of tasks with subsequent tasks, ...
 *   Tasks can come with pre-defined list of zero, one or more subsequent
 *   tasks, which then are scheduled. ..
 *
 * ...
 *
 * 1. Tasks are POJO (Plain Old JavaScript Object),
 * 		Properties:
 * 			type:	required: string,
 * 			then:	optional: null,string,array of strings, array of objects.
 * 					- string: a task type,
 * 					- array of strings: a list of task types,
 * 					- array of objects: a list of tasks.
 * 			scheduleTimeoutMs:		optional: number
 *
 *
 * 2. options.taskHandlers is map of task types to handler functions,
 * 		Alternatively, options.runTask( onCallDone, args ) can be provided.
 *
 *
 * 3. options.createSubSequentTaskArgs( newTask, args ) is used to create subsequent
 * 		tasks, and e.g. pass custom properties from current to future tasks.
 *
 *
 * @param {Object} options
 */
export function ChainedTasksScheduler( options ) {


	// destructure from options, with defaults
	const {
		taskHandlers = { },
		enableDebugLog = false,

		runTask = function defRunTask( onCallDone, args ) {
			const type = args.type;
			if( taskHandlers.hasOwnProperty(type) ) {
				if( enableDebugLog ) {
					// eslint-disable-next-line no-console
					console.log( JSON.parse( JSON.stringify( {
						_w:'ChainedTasksScheduler::defRunTask()',
						...args,
						// _scheduler: this,
					}, DOMElementsStringifyReplacer ) ) );
				}
				taskHandlers[type].call(this,args);
			} else {
				throw new Error('unknown task type: '+type);
			}
			return onCallDone();
		},

		createSubSequentTaskArgs = function defCreateSubSequentTaskArgs( newTask, args ) {
			const argType = typeof newTask;
			if( argType==='object' ) return newTask;
			if( argType==='string' ) return { type:newTask };
			throw new Error('newTask has invalid type: '+argType+'; '+JSON.stringify(newTask));
		},

		defaultTimeoutMs = 4,
		enableDebugInfo = false,
	} = options;



	const wrappedRunTask = function( origOnCallDone, args ) {
		let that = this;

		const wrappedOnCallDone = function() {
			if( args.then ) {
				// schedule subsequent task - provided by args.then ..
				let subSeqTaskObj = null;
				if( typeof args.then === 'string' && args.then.length ) {
					// plain string ? -> means task type
					subSeqTaskObj = createSubSequentTaskArgs(args.then,args);
				} else if( Array.isArray(args.then) ) {
					if( args.then.length ) {
						// array ? -> task list -> pop & handle first task,
						// .. pass remaining tasks to next schedule run,
						// .. let the next run handle the next tasks & so on.
						const subSeqTask = args.then.shift();
						if( typeof subSeqTask === 'string' ) {
							// string ? -> means task type
							subSeqTaskObj = createSubSequentTaskArgs(subSeqTask,args);
							// move remaining "thens" into args of new task ..
							subSeqTaskObj.then = [ ...args.then ];
						} else if( typeof subSeqTask === 'object' ) {
							// object ? -> clone and use as task args
							subSeqTaskObj = createSubSequentTaskArgs(Object.assign({},subSeqTask),args);
							// move & merge remaining "thens" into args of new task ..
							subSeqTaskObj.then = [ ...subSeqTaskObj.then??[], ...args.then??[] ];
						} else {
							throw new Error('invalid subsequent task spec in args.then: '+JSON.stringify(args.then));
						}
					}
				} else {
					throw new Error('invalid args.then: '+JSON.stringify(args.then));
				}
				if( subSeqTaskObj!==null ) {
					const timeoutMs = subSeqTaskObj.scheduleTimeoutMs ?? defaultTimeoutMs;
					// if( enableDebugLog ) {
					// 	// eslint-disable-next-line no-console
					// 	console.dir( JSON.parse( JSON.stringify( {
					// 		w: 'ChainedTasksScheduler scheduling subsequent "then" task',
					// 		timeoutMs,
					// 		subSeqTaskObj,
					// 		scheduler: this,
					// 	}, DOMElementsStringifyReplacer ) ) );
					// }
					that.schedule(timeoutMs,subSeqTaskObj);
				}
			}
			return origOnCallDone();
		};

		return runTask.call(this,wrappedOnCallDone,args);
	};


	// TODO: store options actually merged with defaults...
	this._options = options;


	SimpleFunctionCallScheduler.call(this,wrappedRunTask,enableDebugInfo);
}


ChainedTasksScheduler.prototype = Object.create( SimpleFunctionCallScheduler.prototype );

Object.defineProperty( ChainedTasksScheduler.prototype, 'constructor', {
	value: ChainedTasksScheduler,
	enumerable: false, // so that it does not appear in 'for in' loop
	writable: true
} );

